// Pure helpers for the "אשר והבא" (approve & next) draft-queue flow on
// DocumentViewPage. The queue is the /documents/drafts list fetched when the
// admin enters edit mode on a draft; these helpers only reason about ids so
// they stay trivially testable.

const toIds = (queue) => (Array.isArray(queue) ? queue : []).map(d => String(d && d.id != null ? d.id : d));

// Position of the current draft inside the queue (1-based) plus the total.
// position is null when the current doc isn't in the queue (e.g. it was just
// approved elsewhere and the list refreshed without it).
export function draftPosition(queue, currentId) {
  const ids = toIds(queue);
  const idx = ids.indexOf(String(currentId));
  return { position: idx >= 0 ? idx + 1 : null, total: ids.length };
}

// ── Order (#313) ───────────────────────────────────────────────────────────
// The queue order is a reviewer's choice. 'upload' is the server's own order
// (duplicates first, then newest upload). 'supplier' (the default) works the
// queue one supplier at a time, so consecutive drafts share a layout and the
// supplier's prices (owner, 08/10 and 21/10/2026):
//   1. drafts with no supplier first — they need a person before anything;
//   2. suppliers by their OLDEST draft (created_at), never alphabetical, so
//      no supplier starves;
//   3. inside a supplier: invoices → delivery notes → חשבונית מרכזת (after its
//      notes, which must be approved to be linkable, #227) → credits and
//      returns → the rest; then the document date (parsed — DD/MM/YYYY text
//      never sorts), then upload time, then id.
// A total order: equal rows never trade places between refetches. DraftsPage
// and DocumentViewPage both sort with this, so the list, "N מתוך M" and
// אשר והבא always agree.
export const DRAFT_ORDERS = Object.freeze(['supplier', 'upload']);
export const DEFAULT_DRAFT_ORDER = 'supplier';

const TYPE_RANK = {
  'חשבונית': 0, 'חשבונית/קבלה': 0, 'חשבון עסקה': 0,
  'תעודת משלוח': 1,
  'חשבונית מרכזת': 2,
  'חשבונית זיכוי': 3, 'תעודת החזרה': 3,
};
const typeRank = (docType) => TYPE_RANK[docType] ?? 4;

// DD/MM/YYYY → YYYYMMDD as a number; anything else sorts last.
const DATE_RE = /^(\d{2})\/(\d{2})\/(\d{4})$/;
function dateKey(date) {
  const m = DATE_RE.exec(typeof date === 'string' ? date.trim() : '');
  return m ? Number(`${m[3]}${m[2]}${m[1]}`) : Number.POSITIVE_INFINITY;
}

const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
// Truthy, the same test DraftsPage uses for its "לא זוהה ספק" rows.
const hasSupplier = (d) => !!(d && d.supplier_id);
const createdAt = (d) => (d && d.created_at ? String(d.created_at) : '');

/** The drafts in the reviewer's order; a new array, the input untouched. */
export function sortDraftQueue(drafts, mode = DEFAULT_DRAFT_ORDER) {
  const list = Array.isArray(drafts) ? [...drafts] : [];
  if (mode !== 'supplier') return list;
  const oldest = new Map();
  for (const d of list) {
    if (!hasSupplier(d)) continue;
    const k = String(d.supplier_id);
    const c = createdAt(d);
    if (!oldest.has(k) || c < oldest.get(k)) oldest.set(k, c);
  }
  return list.sort((a, b) => {
    const sa = hasSupplier(a), sb = hasSupplier(b);
    if (sa !== sb) return sa ? 1 : -1;
    if (sa) {
      const ka = String(a.supplier_id), kb = String(b.supplier_id);
      if (ka !== kb) return cmp(oldest.get(ka), oldest.get(kb)) || cmp(Number(a.supplier_id), Number(b.supplier_id)) || cmp(ka, kb);
      const byType = typeRank(a.doc_type) - typeRank(b.doc_type);
      if (byType) return byType;
      const byDate = cmp(dateKey(a.date), dateKey(b.date));
      if (byDate) return byDate;
    }
    return cmp(createdAt(a), createdAt(b)) || cmp(Number(a.id), Number(b.id));
  });
}

/** How many supplier groups the queue holds (no supplier counts as one). */
export function supplierGroupCount(drafts) {
  return new Set((Array.isArray(drafts) ? drafts : []).map(d => (hasSupplier(d) ? String(d.supplier_id) : ''))).size;
}

/**
 * Where the current draft sits inside its supplier's run of a sorted queue:
 * { index (1-based), size, noSupplier } — null when it is not in the queue.
 * Neighbours are compared, so it holds for any order the queue is in.
 */
export function draftGroupPosition(queue, currentId) {
  const list = Array.isArray(queue) ? queue : [];
  const idx = list.findIndex(d => String(d && d.id) === String(currentId));
  if (idx === -1) return null;
  const key = (d) => (hasSupplier(d) ? String(d.supplier_id) : '');
  const k = key(list[idx]);
  let start = idx;
  while (start > 0 && key(list[start - 1]) === k) start--;
  let end = idx;
  while (end < list.length - 1 && key(list[end + 1]) === k) end++;
  return { index: idx - start + 1, size: end - start + 1, noSupplier: k === '' };
}

// Next draft to open after approving currentId. Order follows the queue as
// given (sortDraftQueue, or the server's order). Rules:
//   • current mid-queue  → the entry right after it
//   • current last       → wrap to the first remaining entry
//   • current not listed → first entry (fresh queue, current already gone)
//   • nothing else left  → null (caller returns to /drafts)
// `skipped` ids (דלג, this session) are passed over — the wrap-around would
// otherwise hand a skipped draft straight back. The current id always keeps
// its place, so opening a skipped draft by hand still advances from it.
export function nextDraftId(queue, currentId, skipped = []) {
  const cur = String(currentId);
  const skip = new Set(Array.from(skipped || [], String));
  const ids = toIds(queue).filter(x => x === cur || !skip.has(x));
  if (ids.length === 0) return null;
  const idx = ids.indexOf(cur);
  if (idx === -1) return ids[0];
  const rest = ids.filter((_, i) => i !== idx);
  if (rest.length === 0) return null;
  return ids[idx + 1] ?? rest[0];
}

// How many drafts in the queue, other than the current one, were skipped —
// what the "queue done" toast owes the reviewer when only those remain.
export function skippedRemaining(queue, currentId, skipped = []) {
  const skip = new Set(Array.from(skipped || [], String));
  return toIds(queue).filter(x => x !== String(currentId) && skip.has(x)).length;
}

// Drafts skipped with דלג in this tab. Module state on purpose: it lives as
// long as the page does (a reload starts the queue over), and document ids
// are global, so one set serves every restaurant.
const skippedThisSession = new Set();
export const markDraftSkipped = (id) => { skippedThisSession.add(String(id)); };
export const skippedDraftIds = () => Array.from(skippedThisSession);
export const clearSkippedDrafts = () => { skippedThisSession.clear(); };
